import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useCookieConsent } from "../contexts/CookieConsentContext";
import { PREFERENCES_EVENT } from "../lib/cookieConsent";
import CookiePreferencesModal from "./CookiePreferencesModal";

const A = {
  bg:      "#18211C",
  surface: "#16313A",
  cream:   "#FBFBF9",
  crimson: "#1F7D91",
  gold:    "#E3704F",
  muted:   "#8A877F",
  border:  "#2A5A66",
};

const AUTH_ROUTES = [
  /^\/loginNew$/,
  /^\/register$/,
  /^\/forgot-password$/,
  /^\/reset-password\//,
  /^\/verify-email\//,
];

function isAuthRoute(pathname) {
  return AUTH_ROUTES.some((re) => re.test(pathname));
}

export default function CookieConsentBanner() {
  const { hasConsented, acceptAll, rejectNonEssential } = useCookieConsent();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const location = useLocation();
  const slim = isAuthRoute(location.pathname);

  useEffect(() => {
    const open = () => setPrefsOpen(true);
    window.addEventListener(PREFERENCES_EVENT, open);
    return () => window.removeEventListener(PREFERENCES_EVENT, open);
  }, []);

  if (hasConsented) {
    return <CookiePreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />;
  }

  const btnPrimary = {
    background: A.crimson,
    color: A.cream,
    border: "none",
    padding: slim ? "0.4rem 0.9rem" : "0.55rem 1.15rem",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: slim ? "0.74rem" : "0.82rem",
    letterSpacing: "0.05em",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  const btnOutline = {
    background: "transparent",
    color: A.cream,
    border: `1.5px solid ${A.muted}`,
    padding: slim ? "0.35rem 0.85rem" : "0.5rem 1.1rem",
    borderRadius: 8,
    fontWeight: 600,
    fontSize: slim ? "0.74rem" : "0.82rem",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  const btnLink = {
    background: "none",
    border: "none",
    color: A.gold,
    fontWeight: 600,
    fontSize: slim ? "0.74rem" : "0.82rem",
    textDecoration: "underline",
    cursor: "pointer",
    fontFamily: "inherit",
    padding: 0,
  };

  return (
    <>
      <div
        role="region"
        aria-label="Cookie consent"
        style={{
          position: "fixed",
          left: slim ? "50%" : 0,
          right: slim ? "auto" : 0,
          bottom: slim ? 12 : 0,
          transform: slim ? "translateX(-50%)" : "none",
          zIndex: 900,
          background: A.bg,
          color: A.cream,
          borderTop: slim ? "none" : `1px solid ${A.border}`,
          border: slim ? `1px solid ${A.border}` : "none",
          borderRadius: slim ? 10 : 0,
          padding: slim ? "0.6rem 1rem" : "1.1rem 1.5rem",
          boxShadow: slim
            ? "0 8px 24px rgba(0,0,0,0.45)"
            : "0 -6px 20px rgba(0,0,0,0.25)",
          maxWidth: slim ? "min(640px, calc(100% - 24px))" : "100%",
        }}
      >
        <div
          style={{
            maxWidth: 1300,
            margin: "0 auto",
            display: "flex",
            alignItems: slim ? "center" : "flex-start",
            gap: slim ? 10 : 20,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: slim ? "center" : "flex-start", gap: 12, flex: 1, minWidth: 240 }}>
            {!slim && (
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: A.surface,
                  border: `1px solid ${A.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: A.gold,
                  flexShrink: 0,
                }}
              >
                <Cookie size={20} />
              </div>
            )}
            <div style={{ fontSize: slim ? "0.78rem" : "0.88rem", lineHeight: 1.5, color: A.cream }}>
              {slim ? (
                <>
                  We use cookies for essential features. <Link to="/cookies" style={{ color: A.gold }}>Cookie policy</Link>.
                </>
              ) : (
                <>
                  We use cookies to operate TalentFora, remember your preferences, and improve the
                  service. Strictly necessary cookies are always on. Read our{" "}
                  <Link to="/cookies" style={{ color: A.gold, fontWeight: 600 }}>Cookie Policy</Link>
                  {" "}and{" "}
                  <Link to="/privacy" style={{ color: A.gold, fontWeight: 600 }}>Privacy Policy</Link>.
                </>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {slim ? (
              <>
                <button type="button" style={btnLink} onClick={() => setPrefsOpen(true)}>Preferences</button>
                <button type="button" style={btnPrimary} onClick={acceptAll}>Accept</button>
              </>
            ) : (
              <>
                <button type="button" style={btnLink} onClick={() => setPrefsOpen(true)}>
                  Manage Preferences
                </button>
                <button type="button" style={btnOutline} onClick={rejectNonEssential}>
                  Reject Non-Essential
                </button>
                <button type="button" style={btnPrimary} onClick={acceptAll}>
                  Accept All
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <CookiePreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />
    </>
  );
}
